import React, { useEffect, useState, useCallback, memo, useMemo } from 'react';
import EffectButton from './components/EffectButton';
import {
  Card, CardContent, 
  Grid, 
  Typography,
  Stack, Slider, Box, TextField, MenuItem 
} from '@mui/material';
import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeUp from '@mui/icons-material/VolumeUp';
import EFFECTS from '../../constants';
import { useTranslation } from 'react-i18next';

const EffectsCard = ({ onPlayRemote, onStopRemote, onVolumeChange, sx }) => {
  const { t } = useTranslation();
  const [volume, setVolume] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_effects_volume');
      if (saved !== null && !isNaN(Number(saved))) {
        return Number(saved);
      }
    } catch (e) {}
    return 1;
  });
  const [keySearch, setKeySearch] = useState('');
  const [effectsFiltered, setEffectsFiltered] = useState(EFFECTS);
  const [selectedCategory, setSelectedCategory] = useState('- Todas las etiquetas -');

  // Sincronizar en tiempo real el slider con eventos emitidos desde Desktop o Remote
  useEffect(() => {
    const handleSyncVolume = (e) => {
      if (typeof e.detail?.volume === 'number' && !isNaN(e.detail.volume)) {
        setVolume(e.detail.volume);
      }
    };
    window.addEventListener('garaoke_set_effects_volume', handleSyncVolume);
    return () => {
      window.removeEventListener('garaoke_set_effects_volume', handleSyncVolume);
    };
  }, []);

  const categories = useMemo(() => {
    return Array.from(new Set(EFFECTS.flatMap(effect => effect.categories.map(category => category.name))));
  }, []);

  // Normalizador estilo SQL LIKE: insensible a mayúsculas, minúsculas, tildes y caracteres diacríticos
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const handleCategoryChange = useCallback((event) => {
    setSelectedCategory(event.target.value);
    const searchNormalized = normalizeText(keySearch);

    let effectsAux = searchNormalized.length > 0 ?
      EFFECTS.filter((effect) => {
        const effectNameNormalized = normalizeText(effect.name);
        const categoriesMatch = effect.categories?.some(category => {
          const name = typeof category === 'string' ? category : category?.name;
          return normalizeText(name).includes(searchNormalized);
        });
        return effectNameNormalized.includes(searchNormalized) || categoriesMatch;
      })
      :
      EFFECTS;

    if (event.target.value !== '- Todas las etiquetas -') {
      setEffectsFiltered(effectsAux.filter((effect) => 
        effect.categories?.some(category => {
          const name = typeof category === 'string' ? category : category?.name;
          return name === event.target.value;
        })
      ));
    } else {
      setEffectsFiltered(effectsAux);
    }
  }, [keySearch]);

  const handleVolumeChange = useCallback((event, newValue) => {
    const val = newValue / 100;
    setVolume(val);
    try {
      localStorage.setItem('garaoke_effects_volume', String(val));
    } catch (e) {}
    window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
      detail: { volume: val, _local: true }
    }));
    if (typeof onVolumeChange === 'function') {
      onVolumeChange(val);
    }
  }, [onVolumeChange]);

  const handleResetVolume = useCallback((e) => {
    e?.stopPropagation();
    setVolume(1);
    try {
      localStorage.setItem('garaoke_effects_volume', '1');
    } catch (err) {}
    window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
      detail: { volume: 1, _local: true }
    }));
    if (typeof onVolumeChange === 'function') {
      onVolumeChange(1);
    }
  }, [onVolumeChange]);

  const handleEffectsSearch = useCallback((event) => {
    setKeySearch(event.target.value);
  }, []);

  const filterByKey = useCallback((searchStr) => {
    const searchNormalized = normalizeText(searchStr);
    if (searchNormalized.length > 0) {
      const filtered = EFFECTS.filter((effect) => {
        const effectNameNormalized = normalizeText(effect.name);
        const categoriesMatch = effect.categories?.some(category => {
          const name = typeof category === 'string' ? category : category?.name;
          return normalizeText(name).includes(searchNormalized);
        });
        return effectNameNormalized.includes(searchNormalized) || categoriesMatch;
      });
      setEffectsFiltered(filtered);
    } else {
      setEffectsFiltered(EFFECTS);
    }
    const event = { target: { value: selectedCategory } };
    handleCategoryChange(event);
  }, [handleCategoryChange, selectedCategory]);

  useEffect(() => {
    filterByKey(keySearch);
  }, [keySearch, filterByKey]);

  return (
    <Card 
      elevation={3} 
      style={{ padding: '10px 12px', margin: 0, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }} 
      sx={{ display: { xs: 'block', sm: 'block' }, ...sx }}
    >
      <CardContent style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        {/* Titular en su propia línea independiente estilo ventana SO */}
        <Box sx={{ mb: 0.8, flexShrink: 0 }}>
          <Typography variant="h6" component="div">
            {t('effects.title')}
          </Typography>
        </Box>

        {/* Fila de controles: Buscador (50%) y Selector de categorías (50%) con idéntica altura y ancho constante */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0, width: '100%' }}>
          <TextField
            placeholder={t('effects.searchPlaceholder')}
            onChange={handleEffectsSearch}
            variant="outlined"
            size="small"
            margin="none"
            sx={{ 
              flex: '1 1 50%',
              width: '50%',
              '& .MuiOutlinedInput-root': {
                height: 36,
                fontSize: '0.8rem'
              },
              '& input': { 
                py: 0, 
                fontSize: '0.8rem' 
              } 
            }}
          />
          <TextField
            select
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="outlined"
            size="small"
            margin="none"
            defaultValue=""
            sx={{ 
              flex: '1 1 50%',
              width: '50%',
              '& .MuiOutlinedInput-root': {
                height: 36,
                fontSize: '0.8rem'
              },
              '& .MuiSelect-select': { 
                py: 0, 
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              } 
            }}
          >
            <MenuItem key={'todas'} value={'- Todas las etiquetas -'}>
              {t('effects.allCategories')}
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <div id="effectsList" className="effects_container scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', margin: '4px 0' }}>
          {effectsFiltered.map((effect) => (
            <EffectButton 
              key={effect.id} 
              effect={effect} 
              volume={volume} 
              setCategory={handleCategoryChange} 
              onPlayRemote={onPlayRemote}
              onStopRemote={onStopRemote}
            />
          ))}
        </div>
        <Box 
          sx={{ 
            width: '100%', 
            flexShrink: 0, 
            mt: 0.5,
            px: 1.5,
            py: 0.8,
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            boxSizing: 'border-box'
          }}
        >
          {/* Icono de volumen alineado */}
          <Box sx={{ display: 'flex', alignItems: 'center', color: volume === 0 ? 'rgba(255, 255, 255, 0.3)' : '#ff2d9b' }}>
            <VolumeDown sx={{ fontSize: 18 }} />
          </Box>

          {/* Slider alineado y centrado */}
          <Slider 
            size="small" 
            aria-label={t('effects.volume')} 
            min={0}
            max={200}
            step={1}
            value={Math.round(volume * 100)} 
            onChange={handleVolumeChange} 
            onDoubleClick={handleResetVolume}
            slotProps={{
              thumb: {
                onDoubleClick: handleResetVolume,
                title: "Doble clic para restablecer al 100%"
              }
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={(val) => `${val}%`}
            sx={{ 
              flex: 1,
              py: 1,
              color: '#ff2d9b',
              height: 5,
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: '#ffffff',
                boxShadow: '0 0 8px #ff2d9b',
                transition: 'box-shadow 0.15s ease',
                top: '50%',
                transform: 'translate(-50%, -50%) !important',
                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                  transform: 'translate(-50%, -50%) !important',
                  boxShadow: '0 0 12px #ff2d9b'
                }
              },
              '& .MuiSlider-track': {
                background: 'linear-gradient(90deg, #ff2d9b 0%, #b44dff 100%)',
                border: 'none',
                borderRadius: 4
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                borderRadius: 4
              },
              '& .MuiSlider-valueLabel': {
                background: 'rgba(18, 8, 32, 0.95)',
                border: '1px solid rgba(255, 45, 155, 0.4)',
                borderRadius: '6px',
                fontSize: '0.72rem',
                padding: '2px 6px'
              }
            }} 
          />

          {/* Badge de porcentaje perfectamente alineado */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              px: 0.8,
              py: 0.3,
              minWidth: 44,
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', color: '#ffffff', fontWeight: 700, lineHeight: 1 }}>
              {Math.round(volume * 100)}%
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default memo(EffectsCard);
